import moment from 'moment-timezone';
import { getFullName } from '@/utilities/associate';

/**
 * Creates a function to use as callback for sort operation, makes the comparisson
 * on object String field
 * @param {String} propName: A string representing Object properties, could be nested
 * props if needed.
 * ie: { name: 'Jonh', address: { country: 'United States', city: 'New York' } }
 * to access country, we could pass 'address.country' as argument
 * @returns {Function} callback to use for sort operation
 */
export function sortByStringProp(propName='', order='ascending') {
  const props = propName.split('.');

  return (a, b) => {
    let valueA = props.reduce((obj, prop) => obj? obj[prop]: obj, a);
    let valueB = props.reduce((obj, prop) => obj? obj[prop]: obj, b);

    if (!valueA && !valueB) return 0;
    if (!valueA) return -1;
    if (!valueB) return 1;

    valueA = valueA.toString().toLowerCase();
    valueB = valueB.toString().toLowerCase();
    if(order === 'descending') return valueB?.localeCompare(valueA);
    return valueA?.localeCompare(valueB);
  }
}

/**
 * Creates a function to use as callback for sort operation, makes the comparisson
 * on object Number field
 * @param {String} propName: A string representing Object properties, could be nested
 * props if needed.
 * ie: { name: 'Jonh', address: { country: 'United States', city: 'New York' } }
 * to access country, we could pass 'address.country' as argument
 * @returns {Function} callback to use for sort operation
 */
export function sortByNumberProp(propName='',order = 'ascending') {
  const props = propName.split('.');

  return (a, b) => {
    let valueA = props.reduce((obj, prop) => (obj ? obj[prop] : obj), a);
    let valueB = props.reduce((obj, prop) => (obj ? obj[prop] : obj), b);

    valueA = Number(valueA);
    valueB = Number(valueB);

    if (isNaN(valueA) && isNaN(valueB)) return 0;
    if (isNaN(valueA)) return -1;
    if (isNaN(valueB)) return 1;

    if (order === 'descending') {
      return valueB - valueA;
    } else {
      return valueA - valueB;
    }
  };
}

/**
 * Creates a function to use as callback for sort operation, makes the comparisson
 * on object Number field
 * @param {String} propName: A string representing Object properties, could be nested
 * props if needed.
 * ie: { name: 'Jonh', address: { country: 'United States', city: 'New York' } }
 * to access country, we could pass 'address.country' as argument
 * @returns {Function} callback to use for sort operation
 */
export function sortByPercentProp(propName='',order = 'ascending') {
  const props = propName.split('.');

  return (a, b) => {
    let valueA = props.reduce((obj, prop) => (obj ? obj[prop] : obj), a);
    let valueB = props.reduce((obj, prop) => (obj ? obj[prop] : obj), b);

    valueA = parseFloat(valueA);
    valueB = parseFloat(valueB);

    if (isNaN(valueA) && isNaN(valueB)) return 0;
    if (isNaN(valueA)) return -1;
    if (isNaN(valueB)) return 1;

    if (order === 'descending') {
      return valueB - valueA;
    } else {
      return valueA - valueB;
    }
  };
}

/**
 * Creates a function to use as callback for sort operation, makes the comparisson
 * on object timestamp field
 * @param {String} propName: A string representing Object properties, could be nested
 * props if needed.
 * ie: { name: 'Jonh', address: { country: 'United States', city: 'New York' } }
 * to access country, we could pass 'address.country' as argument
 * @returns {Function} callback to use for sort operation
 */
export function sortByDateProp(propName='',order = 'ascending') {
  const props = propName.split('.');

  return (a, b) => {
    const timestampA = props.reduce((obj, prop) => (obj ? obj[prop] : obj), a);
    const timestampB = props.reduce((obj, prop) => (obj ? obj[prop] : obj), b);

    if (timestampA === null && timestampB === null) return 0;
    if (timestampA === null) return order === 'ascending' ? -1 : 1;
    if (timestampB === null) return order === 'ascending' ? 1 : -1;

    const dateA = moment(timestampA);
    const dateB = moment(timestampB);

    if (order === 'descending') {
      if (dateB.isBefore(dateA)) return -1;
      if (dateB.isAfter(dateA)) return 1;
      return 0;
    } else {
      if (dateA.isBefore(dateB)) return -1;
      if (dateA.isAfter(dateB)) return 1;
      return 0;
    }
  };
}

const sortByTime = (a, b) => {
    if ((!a.time || a.time === '') && (b.time && b.time !== '')) {
        return -1;
    }
    if ((a.time && a.time !== '') && (!b.time || b.time === '')) {
        return 1;
    }
    
    if (a.time && b.time) {
        const timeCompare = a.time.localeCompare(b.time);
        if (timeCompare !== 0) return timeCompare;
    }

    return new Date(b.createdAt) - new Date(a.createdAt);
}

const sortByStaffName = (a, b) => {
    if (!a.staff && !b.staff) return 0;
    if (!a.staff) return 1;
    if (!b.staff) return -1;
    const aFullName = getFullName(a.staff).toLowerCase();
    const bFullName = getFullName(b.staff).toLowerCase();
    return aFullName.localeCompare(bFullName, undefined, {numeric: true, sensitivity: 'base'})
}

const sortByDeviceName = (a,b) => {      
    return a?.device?.deviceName?.toLowerCase().localeCompare(b?.device?.deviceName?.toLowerCase(), undefined, {numeric: true, sensitivity: 'base'})
}

const sortByVehicleName = (a,b) => {   
    const vehicleA = a.vehicle;
    const vehicleB = b.vehicle;
    if (!vehicleA?.name) return -1;
    if (!vehicleB?.name) return 1;
    return vehicleA.name.toLowerCase().localeCompare(vehicleB.name.toLowerCase(), undefined, {numeric: true, sensitivity: 'base'})
}

const sortByRosterStatus = (a, b) => {   
    const aStatus = a.status && a.status.id ? a.status.option : 'z'
    const bStatus = b.status && b.status.id ? b.status.option : 'z'

    return (aStatus.toLowerCase()).localeCompare(bStatus.toLowerCase())
}

const sortByParkingSpace = (a, b) => {
    const aParking = a?.parkingSpace?.option || 'z';
    const bParking = b?.parkingSpace?.option || 'z';
    return (aParking.toLowerCase()).localeCompare(bParking.toLowerCase());
}

export const sorting = (routes, sortProp, sortOrder) => {
    return routes.sort((a, b) => {
        let sorted = []
        switch (sortProp) {
            case 'time':
                sorted = sortByTime(a, b);
                break;
            case 'staff':
                sorted = sortByStaffName(a, b);
                break;
            case 'vehicle':
                sorted = sortByVehicleName(a, b);
                break;
            case 'device':
                sorted = sortByDeviceName(a, b);
                break;
            case 'parkingSpace':
                sorted = sortByParkingSpace(a, b);
                break;
            case 'status':
                sorted = sortByRosterStatus(a, b);
                break;
            default:
                sorted = sortByTime(a, b);
                break
        }

        return sortOrder === 'descending' ? -sorted : sorted;
    });
};
